// Task scope — was this work in the quote? One value per task, and billing follows from it (Lj, 2026-10-08:
// "the quote is the plan").
//   included  in the quote: paid through the contract
//   extra     not in the quote, the client pays: billed on top (change order)
//   missed    should have been in the quote: ours, never billed — an estimating lesson
//   overhead  tracked, never priced on purpose: travel inside the free area, gas, admin
// A quoted job (a contract value) gets defaults so it is rarely touched by hand: the tasks that existed
// when work started are the quote (included); anything added after is unsorted ("sort me") until a tap.
// Pure. No I/O.

export const SCOPES = ['included', 'extra', 'missed', 'overhead'];
export const SCOPE_LABEL = { included: 'Included', extra: 'Extra', missed: 'Missed', overhead: 'Overhead' };
export const SCOPE_HINT = {
  included: 'In the quote: paid through the contract',
  extra: 'Not in the quote: the client pays (change order)',
  missed: 'Should have been in the quote: ours, not billed',
  overhead: 'Tracked, never priced: travel, gas, admin',
};
// Old free tags read as a scope until one is set (#extra was used before this existed).
const TAG_SCOPE = { extra: 'extra', missed: 'missed', overhead: 'overhead', travel: 'overhead' };

const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
export const isScope = (v) => SCOPES.includes(v);

/** Does time in this scope earn money (the contract or a change order)? null = no scope. */
export function scopeEarns(scope) {
  if (!isScope(scope)) return null;
  return scope === 'included' || scope === 'extra';
}

/** The task patch for a scope pick: the scope, and billable follows it. null clears the pick (billable left alone). */
export function scopePatch(scope) {
  if (scope === null || scope === undefined || scope === '') return { scope: null };
  if (!isScope(scope)) throw new Error(`unknown scope "${scope}" (${SCOPES.join(' · ')})`);
  return { scope, billable: scopeEarns(scope) };
}

/**
 * What an hour of extra bills at (Lj, 11-01: "based on project and user settings"): the project's extras rate, else its
 * one bill rate, else the person's bill rate by role. `pay` = their opsPay doc, `roles` = opsRates/billRoles.
 * → { rate, from: 'project' | 'role' | null }; rate null = nothing set, which is not $0.
 */
export function extraRateFor(project, pay = null, roles = []) {
  const own = num(project?.extraRate) || num(project?.hourlyRate);
  if (own > 0) return { rate: own, from: 'project' };
  const role = pay?.billRole ? (roles || []).find((r) => r && r.id === pay.billRole) : null;
  if (num(role?.billRate) > 0) return { rate: num(role.billRate), from: 'role' };
  return { rate: null, from: null };
}

/**
 * A job's extras. → { rate, from, ms, value, toBillMs, toBill, rows: [{ task, ms, value, clientOk, billed }] }: every
 * task scoped extra (by pick or old tag), its banked time, at the rate; billed ones stay listed, out of `toBill`.
 */
export function extrasOf(tasks = [], project, { pay = null, roles = [], planAt = null } = {}) {
  const mine = tasks.filter((t) => t && t.projectId === project?.id && !t.deletedAt);
  const byId = new Map(mine.map((t) => [t.id, t]));
  const { rate, from } = extraRateFor(project, pay, roles);
  const worth = (ms) => (rate ? Math.round((ms / 3600000) * rate * 100) / 100 : null);
  const rows = mine.filter((t) => !t.parentId && effectiveScope(t, { planAt, byId }) === 'extra').map((task) => {
    const ms = num(task.timeSpent);
    return { task, ms, value: worth(ms), clientOk: task.clientOk ?? null, billed: !!task.extraBilled };
  }).sort((a, b) => b.ms - a.ms);
  const sum = (list) => list.reduce((a, r) => a + r.ms, 0);
  const open = rows.filter((r) => !r.billed);
  return { rate, from, ms: sum(rows), value: worth(sum(rows)), toBillMs: sum(open), toBill: worth(sum(open)), rows };
}

/** Lines to put on an invoice for these extras: hours to two places at the rate. No rate = no lines. */
export function extraInvoiceLines(rows = [], rate) {
  if (!(num(rate) > 0)) return [];
  return rows.filter((r) => r.ms > 0 && !r.billed).map((r) => ({ title: 'Extra', description: String(r.task?.title || 'Extra'), qty: Math.round((r.ms / 3600000) * 100) / 100, unitPrice: num(rate) }));
}

/**
 * When a quoted job's plan was set: the tasks that existed then are the quote. An explicit project.planLockedAt
 * wins. Otherwise a job with a contract value starts its plan when a quoted line (a task with an estimate) first gets
 * time: the estimating before it isn't the job, and a plan added after estimating is still the quote (Ken: first
 * session Oct 14, plan added Oct 30, 39 of 43 tasks read "sort me"). Quoted lines with no time yet = not started
 * (null). No `tasks`, or no quoted lines: its first work session. null = not a quoted job.
 */
export function planStartFor(project, sessions = [], tasks = null) {
  if (!project) return null;
  if (Number.isFinite(project.planLockedAt)) return project.planLockedAt;
  if (!(num(project.value) > 0)) return null;
  const quoted = (tasks || []).filter((t) => t && t.projectId === project.id && num(t.estimatedMinutes) > 0 && !t.bucket);
  if (quoted.length) {
    let start = null;
    for (const t of quoted) for (const e of t.timeEntries || []) {
      const at = e?.date ? new Date(`${e.date}T${e.startTime || '00:00'}:00`).getTime() : NaN;
      if (Number.isFinite(at) && (start === null || at < start)) start = at;
    }
    return start;
  }
  let first = null;
  for (const s of sessions) {
    if (s.projectId !== project.id || s.overlay || s.type === 'travel' || !Number.isFinite(s.startedAt)) continue;
    if (first === null || s.startedAt < first) first = s.startedAt;
  }
  return first;
}

/**
 * A task's scope: its own pick; else a subtask follows its parent; else an old tag (#extra, #travel…); else, on a
 * quoted job, included when it existed before work started. null = no scope (not a quoted job, or unsorted).
 * opts: { planAt, byId (Map id → task, for parents) }
 */
export function effectiveScope(task, { planAt = null, byId = null } = {}) {
  if (!task) return null;
  if (isScope(task.scope)) return task.scope;
  const parent = task.parentId && byId ? byId.get(task.parentId) : null;
  if (parent && parent.id !== task.id) {
    const p = effectiveScope(parent, { planAt, byId: null });
    if (p) return p;
  }
  for (const tag of task.tags || []) if (TAG_SCOPE[tag]) return TAG_SCOPE[tag];
  if (Number.isFinite(planAt) && num(task.createdAt) > 0 && task.createdAt <= planAt) return 'included';
  return null;
}

/** On a quoted job, a task with no scope yet needs a tap ("sort me"). Setup / cleanup checklist items never do. */
export function needsScope(task, opts = {}) {
  return Number.isFinite(opts.planAt) && !task?.bucket && !effectiveScope(task, opts);
}

/**
 * Time and task counts per scope for one project's tasks (each task's own banked time).
 * @returns {{ quoted: boolean, included, extra, missed, overhead, unsorted, none, earningMs, billedOnTopMs, leakMs }}
 *   each bucket { ms, tasks }; unsorted = a quoted job's tasks with no scope yet; none = not a quoted job.
 */
export function scopeTotals(tasks = [], { planAt = null } = {}) {
  const byId = new Map(tasks.map((t) => [t.id, t]));
  const quoted = Number.isFinite(planAt);
  const out = { quoted };
  for (const k of [...SCOPES, 'unsorted', 'none']) out[k] = { ms: 0, tasks: 0 };
  for (const t of tasks) {
    const s = effectiveScope(t, { planAt, byId });
    const k = s || (quoted && !t.bucket ? 'unsorted' : 'none');
    out[k].ms += num(t.timeSpent);
    out[k].tasks += 1;
  }
  out.earningMs = out.included.ms + out.extra.ms;
  out.billedOnTopMs = out.extra.ms;
  out.leakMs = out.missed.ms;
  return out;
}
